import Global from '../common/global';
import { showSnackbar } from '../common/snackBar';

export class CustomerFavouriteManager {
    private static readonly addButtonSelector = ".btn-add-favourite";
    private static readonly removeButtonSelector = ".btn-remove-favourite";

    public static init(): void {
        // Use event delegation on a parent container (document or a specific wrapper)
        document.addEventListener("click", (event) => {
            const button = (event.target as HTMLElement).closest("button");
            if (!button) return;

            const providerId = button.dataset.providerId;
            if (!providerId) return;

            if (button.classList.contains("btn-add-favourite")) {
                event.preventDefault();
                CustomerFavouriteManager.addFavourite(button as HTMLButtonElement, providerId)
                    .then((result) => {
                        if (result.success && !result.notLoggedIn) {
                            showSnackbar(window._screenMessages.Success, window._screenMessages.HotelAddedToYourFavourites);
                        }
                    });
            } else if (button.classList.contains("btn-remove-favourite")) {
                event.preventDefault();
                CustomerFavouriteManager.removeFavourite(button as HTMLButtonElement, providerId)
                    .then((result) => {
                        if (result.success && !result.notLoggedIn) {
                            showSnackbar(window._screenMessages.Success, window._screenMessages.HotelRemovedFromYourFavourites);
                        }
                    });
            }
        });
    }

    private static async addFavourite(button: HTMLButtonElement, providerId: string): Promise<{ success: boolean; notLoggedIn: boolean }> {
        try {
            const response = await fetch("/Account/AddFavourite", {
                method: "POST",
                headers: {
                    "Content-Type": "application/x-www-form-urlencoded",
                    "RequestVerificationToken": Global.getAntiForgeryToken(),
                },
                body: new URLSearchParams({
                    providerId: providerId.toString(),
                    returnUrl: window.location.href
                }),
            });

            if (!response.ok) throw new Error("Network response was not ok");

            const data = (await response.json()) as { success: boolean; favouriteId?: string; notLoggedIn?: boolean; redirectUrl?: string };

            if (data.notLoggedIn && data.redirectUrl) {
                // Redirect to login
                window.location.href = data.redirectUrl;
                return { success: false, notLoggedIn: true };
            }

            if (data.success) {
                const icon = button.querySelector("i");
                if (icon) {
                    icon.classList.toggle("icon-heart-filled");
                    icon.classList.toggle("icon-heart");
                }

                // Toggle button classes
                button.classList.toggle("btn-add-favourite");
                button.classList.toggle("btn-remove-favourite");
            }

            return { success: data.success, notLoggedIn: !!data.notLoggedIn };
        } catch (error) {
            console.error("Error adding favourite:", error);
            alert("Something went wrong. Please try again.");
            return { success: false, notLoggedIn: true };
        }
    }

    private static async removeFavourite(button: HTMLButtonElement, providerId: string): Promise<{ success: boolean; notLoggedIn: boolean }> {
        try {
            const response = await fetch("/Account/RemoveFavourite", {
                method: "POST",
                headers: {
                    "Content-Type": "application/x-www-form-urlencoded",
                    "RequestVerificationToken": Global.getAntiForgeryToken(),
                },
                body: new URLSearchParams({ providerId: providerId.toString() }),
            });

            if (!response.ok) throw new Error("Network response was not ok");

            const data = (await response.json()) as { success: boolean; favouriteId?: string; notLoggedIn?: boolean; redirectUrl?: string };

            if (data.notLoggedIn && data.redirectUrl) {
                // Redirect to login
                window.location.href = data.redirectUrl;
                return { success: false, notLoggedIn: true };
            }

            if (data.success) {
                const icon = button.querySelector("i");
                if (icon) {
                    icon.classList.toggle("icon-heart-filled");
                    icon.classList.toggle("icon-heart");
                }

                // Toggle button classes
                button.classList.toggle("btn-add-favourite");
                button.classList.toggle("btn-remove-favourite");

                // --- 👇 Hide parent .hotel-list-card if inside .db-customer-favourites ---
                const page = button.closest(".db-customer-favourites");
                if (page) {
                    const card = button.closest(".hotel-list-card");
                    if (card instanceof HTMLElement) {
                        card.style.display = "none";

                        // --- Check if any visible cards remain ---
                        const visibleCards = page.querySelectorAll(".hotel-list-card:not([style*='display: none'])");
                        if (visibleCards.length === 0) {
                            const emptyState = page.querySelector(".empty-state") as HTMLElement | null;
                            const wrapper = page.querySelector(".customer-favourites-wrapper") as HTMLElement | null;

                            if (emptyState) emptyState.classList.remove("hidden");
                            if (wrapper) wrapper.classList.add("hidden");
                        }
                    }
                }
            }

            return { success: data.success, notLoggedIn: !!data.notLoggedIn };
        } catch (error) {
            console.error("Error removing favourite:", error);
            alert("Something went wrong. Please try again.");
            return { success: false, notLoggedIn: true };
        }
    }
}
